import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createSave, deleteSave, listSaves } from "../api/game";
import { errorMessage } from "../api/client";
import { useAuth } from "../app/AuthContext";
import { formatGameDate } from "../components/GameUi";
import type { SaveSummary } from "../shared/api-types";

const SAVE_LIMIT = 5;

export function SaveLobbyPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const createDialog = useRef<HTMLDialogElement>(null);
  const deleteDialog = useRef<HTMLDialogElement>(null);
  const [saves, setSaves] = useState<SaveSummary[]>([]);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [error, setError] = useState<string | null>(null);
  const [civilizationName, setCivilizationName] = useState("");
  const [creationKey, setCreationKey] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<SaveSummary | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    void listSaves().then((result) => {
      if (!active) return;
      setSaves(result.saves);
      setStatus("ready");
    }).catch((cause) => {
      if (!active) return;
      setError(errorMessage(cause));
      setStatus("error");
    });
    return () => { active = false; };
  }, []);

  function openCreate() {
    setError(null);
    const displayName = auth.user?.displayName ?? "新生";
    setCivilizationName((displayName.endsWith("文明") ? displayName : `${displayName}文明`).slice(0, 40));
    setCreationKey(crypto.randomUUID());
    createDialog.current?.showModal();
  }

  async function create(event: React.FormEvent) {
    event.preventDefault();
    if (!auth.csrfToken || !creationKey || civilizationName.trim().length < 2) return;
    setBusy(true);
    setError(null);
    try {
      const save = await createSave(civilizationName.trim(), auth.csrfToken, creationKey);
      setSaves((current) => [save, ...current]);
      createDialog.current?.close();
      navigate(`/play/${save.id}/overview`);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  function askDelete(save: SaveSummary) {
    setError(null);
    setDeleteTarget(save);
    deleteDialog.current?.showModal();
  }

  async function remove() {
    if (!deleteTarget || !auth.csrfToken) return;
    setBusy(true);
    setError(null);
    try {
      await deleteSave(deleteTarget.id, auth.csrfToken);
      setSaves((current) => current.filter((save) => save.id !== deleteTarget.id));
      deleteDialog.current?.close();
      setDeleteTarget(null);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function signOut() {
    setBusy(true);
    try {
      await auth.signOut();
      navigate("/login", { replace: true });
    } catch (cause) {
      setError(errorMessage(cause));
      setBusy(false);
    }
  }

  return (
    <main className="save-lobby">
      <header className="save-lobby-header">
        <div className="brand"><span className="brand-mark">SC</span><div><b>STELLAR COLONY</b><span>SAVE ARCHIVE</span></div></div>
        <div className="save-account"><span>{auth.user?.displayName}</span><button className="button ghost compact" onClick={() => void signOut()} disabled={busy}>退出登录</button></div>
      </header>

      <section className="game-mode-selector" aria-labelledby="game-mode-heading">
        <header><p className="eyebrow">SHARED ACCOUNT // SEPARATE UNIVERSES</p><h2 id="game-mode-heading">选择游戏入口</h2><p>两款游戏共用当前账号与安全会话，资源、规则和长期进度彼此独立。</p></header>
        <div className="game-mode-grid">
          <article className="game-mode-card colony-mode" data-game-mode="stellar-colony">
            <span className="game-mode-mark">SC</span>
            <div><small>文明经营 · 长周期战略</small><h3>星际殖民</h3><p>勘测、开发、科研与舰队组成的多存档星际文明。</p><ul><li>至多 5 个文明存档</li><li>服务器离线结算</li></ul></div>
            <a className="button" href="#colony-saves">管理文明存档</a>
          </article>
          <article className="game-mode-card stellar-mode" data-game-mode="stellar-genesis">
            <span className="game-mode-mark stellar-mark-mini" aria-hidden="true">◎</span>
            <div><small>纯点击挂机 · 恒星物理</small><h3>星核纪元</h3><p>从分子云到恒星遗迹，用质量、聚变与 H–R 图塑造跨代演化。</p><ul><li>一账号一条跨代进度</li><li>点击即时反馈与实时存档</li></ul></div>
            <button className="button primary" type="button" onClick={() => navigate("/stellar")}>进入星核纪元</button>
          </article>
        </div>
      </section>

      <section className="save-hero" id="colony-saves">
        <div><p className="eyebrow">COMMAND ARCHIVE</p><h1>选择你的文明</h1><p>每个存档拥有独立世界、资源网络与长期进度。最多同时保留 {SAVE_LIMIT} 个。</p></div>
        <button className="button primary" onClick={openCreate} disabled={busy || saves.length >= SAVE_LIMIT}>+ 新建存档</button>
      </section>

      <section className="save-intro" aria-label="存档操作说明">
        <span><b>1 · 新建</b><small>为文明命名并生成独立星区</small></span>
        <span><b>2 · 进入</b><small>挂机进度会在打开存档时结算</small></span>
        <span><b>3 · 管理</b><small>删除只影响选中的存档</small></span>
      </section>

      {error && <div className="feedback-banner danger" role="alert"><span>{error}</span><button className="button compact ghost" onClick={() => setError(null)}>关闭</button></div>}
      {status === "loading" ? (
        <div className="loading-panel" role="status"><span className="loading-bar" />正在读取存档索引…</div>
      ) : status === "error" ? (
        <div className="error-panel"><h2>存档索引载入失败</h2><button className="button" onClick={() => window.location.reload()}>重新载入</button></div>
      ) : (
        <section className="save-grid" aria-label={`存档列表，已使用 ${saves.length}/${SAVE_LIMIT}`}>
          {saves.map((save) => (
            <article className="save-card" key={save.id}>
              <header><span className="save-slot">SLOT {String(save.slotIndex).padStart(2, "0")}</span><span className="status-chip">{save.corrupt ? "需要处理" : `文明发展 ${save.civilizationLevel}级`}</span></header>
              <div><h2>{save.civilizationName}</h2><p>{formatGameDate(save.gameTimeYears)}</p></div>
              <dl><div><dt>最近指挥</dt><dd>{new Date(save.updatedAt).toLocaleString("zh-CN", { dateStyle: "medium", timeStyle: "short" })}</dd></div><div><dt>建立日期</dt><dd>{new Date(save.createdAt).toLocaleDateString("zh-CN")}</dd></div></dl>
              <div className="save-card-actions"><button className="button primary" aria-label={save.corrupt ? `${save.civilizationName}存档不可读取` : `进入文明：${save.civilizationName}`} onClick={() => navigate(`/play/${save.id}/overview`)} disabled={save.corrupt}>{save.corrupt ? "存档不可读取" : "进入文明"}</button><button className="button ghost danger-text" aria-label={`删除存档：${save.civilizationName}`} onClick={() => askDelete(save)}>删除</button></div>
            </article>
          ))}
          {saves.length < SAVE_LIMIT && (
            <button className="empty-save-slot" onClick={openCreate}><span>+</span><b>建立新文明</b><small>剩余 {SAVE_LIMIT - saves.length} 个存档位</small></button>
          )}
        </section>
      )}

      <dialog ref={createDialog} className="save-dialog" aria-labelledby="create-save-heading" onClose={() => setError(null)}>
        <form className="form-stack" onSubmit={create}>
          <div><p className="eyebrow">NEW WORLD</p><h2 id="create-save-heading">新建存档</h2><p>输入文明名称，系统会生成一片全新的独立星区。</p></div>
          {error && <div className="alert danger" role="alert">{error}</div>}
          <label><span className="field-label">文明名称</span><input autoFocus required minLength={2} maxLength={40} value={civilizationName} onChange={(event) => setCivilizationName(event.target.value)} /></label>
          <div className="dialog-actions"><button className="button ghost" type="button" onClick={() => createDialog.current?.close()} disabled={busy}>取消</button><button className="button primary" disabled={busy || civilizationName.trim().length < 2}>{busy ? "正在生成…" : "生成世界并进入"}</button></div>
        </form>
      </dialog>

      <dialog ref={deleteDialog} className="save-dialog danger-zone" aria-labelledby="delete-save-heading" onClose={() => setDeleteTarget(null)}>
        <div className="form-stack"><div><p className="eyebrow">IRREVERSIBLE</p><h2 id="delete-save-heading">删除“{deleteTarget?.civilizationName}”？</h2><p>该存档的世界、舰队与挂机进度会永久删除，其他存档不受影响。</p></div>{error && <div className="alert danger" role="alert">{error}</div>}<div className="dialog-actions"><button className="button ghost" onClick={() => deleteDialog.current?.close()} disabled={busy}>保留存档</button><button className="button danger-button" onClick={() => void remove()} disabled={busy}>{busy ? "正在删除…" : "确认删除"}</button></div></div>
      </dialog>
    </main>
  );
}
